import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Video, Eye, Calendar, Share2, Copy, User, Building,
  Linkedin, Twitter, Facebook, Loader2, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";

export default function VideoPlayback() {
  const { shareId } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchVideo();
  }, [shareId]);

  const fetchVideo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/videos/share/${shareId}`);
      setVideo(response.data);
    } catch (err) {
      setError('Video not found or has been removed');
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="loading-state">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#C41E3A] mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Video Not Found</h1>
          <p className="text-zinc-500 mb-6">{error}</p>
          <Link to="/" className="btn-primary rounded-sm">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="video-playback-page">
      {/* Video Section */}
      <div className="bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="relative aspect-video">
            <video
              src={`${API_URL}${video.video_url}`}
              controls
              autoPlay
              className="w-full h-full"
              data-testid="video-player"
            />
            {/* Watermark */}
            {video.has_watermark && (
              <div className="absolute bottom-4 right-4 text-white/50 text-sm font-bold pointer-events-none" data-testid="video-watermark">
                BrandStage
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-2xl md:text-3xl font-black mb-4" data-testid="video-title">
          {video.title}
        </h1>

        {video.description && (
          <p className="text-zinc-400 mb-6" data-testid="video-description">
            {video.description}
          </p>
        )}

        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500 mb-8">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{video.user_name}</span>
          </div>
          {video.user_company && (
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              <span>{video.user_company}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span>{video.views} views</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(video.created_at)}</span>
          </div>
        </div>

        {/* Share Section */}
        <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-sm mb-8" data-testid="share-section">
          <h2 className="font-bold mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#C41E3A]" />
            Share This Video
          </h2>
          
          {/* Copy link */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="flex-1 bg-zinc-950 border border-zinc-700 px-4 py-2 rounded-sm text-sm"
              data-testid="share-url-input"
            />
            <button
              onClick={copyLink}
              className={`px-4 py-2 rounded-sm font-medium transition-colors ${
                copied ? 'bg-green-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700'
              }`}
              data-testid="copy-button"
            >
              {copied ? 'Copied!' : <Copy className="w-5 h-5" />}
            </button>
          </div>

          {/* Social share buttons */}
          <div className="flex gap-3">
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white rounded-sm text-sm font-medium hover:bg-[#006396] transition-colors"
              data-testid="share-linkedin"
            >
              <Linkedin className="w-4 h-4" />
              LinkedIn
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(video.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] text-white rounded-sm text-sm font-medium hover:bg-[#0c8ddb] transition-colors"
              data-testid="share-twitter"
            >
              <Twitter className="w-4 h-4" />
              Twitter
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#4267B2] text-white rounded-sm text-sm font-medium hover:bg-[#365899] transition-colors"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4" />
              Facebook
            </a>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#C41E3A]/20 to-transparent border border-[#C41E3A]/30 p-6 rounded-sm" data-testid="cta-section">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">Create Your Own Quick Bite</h3>
              <p className="text-zinc-400 text-sm">
                Record professional video messages and share them on social media in minutes.
              </p>
            </div>
            <Link
              to="/studio"
              className="btn-primary rounded-sm flex items-center gap-2 whitespace-nowrap"
              data-testid="cta-record"
            >
              <Video className="w-5 h-5" />
              Start Recording
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt="BrandStage" className="h-8 w-auto mx-auto" />
          </Link>
          <p className="text-zinc-600 text-sm mt-2">
            Professional video storytelling for leaders
          </p>
        </div>
      </div>
    </div>
  );
}
